import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Modal, PasscodeInput, Dialog, Button, Popup } from "antd-mobile";
import { TextDeletionOutline, DownOutline } from "antd-mobile-icons";
import { useStores } from "../../models";
import { ToastError, ToastSuccess } from "../Toast";

type PasscodeInputRef = {
  focus: () => void;
  blur: () => void;
};

type Props = {
  visible: boolean;
  onClose?: () => void;
  setVisible?: (v: boolean) => void;
  onSuccess?: () => void;
  callBack?: (ok: boolean) => void;
};

/** PC 可点击的数字键盘（antd-mobile NumberKeyboard 仅监听 touch，桌面点按无效） */
const ClickNumberKeyboard = ({
  visible,
  onInput,
  onDelete,
  onClose,
}: {
  visible?: boolean;
  onInput?: (v: string) => void;
  onDelete?: () => void;
  onClose?: () => void;
}) => {
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "BACKSPACE"];

  const press = (key: string) => {
    if (!key) return;
    if (key === "BACKSPACE") onDelete?.();
    else onInput?.(key);
  };

  return (
    <Popup
      visible={!!visible}
      position="bottom"
      mask={false}
      onMaskClick={onClose}
      style={{ "--z-index": "1100" } as CSSProperties}
      bodyStyle={{ padding: 0, background: "#f5f5f5" }}
    >
      {/* preventDefault：点虚拟键盘时不抢走 PasscodeInput 焦点，实体键盘可继续输入 */}
      <div
        onMouseDown={(e) => e.preventDefault()}
        style={{ userSelect: "none" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            height: 40,
            padding: "0 16px",
            background: "#f5f5f5",
            borderBottom: "1px solid #eee",
          }}
        >
          <span
            role="button"
            onClick={onClose}
            style={{ cursor: "pointer", display: "inline-flex", padding: 4 }}
          >
            <DownOutline />
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1,
            background: "#ddd",
            borderTop: "1px solid #ddd",
          }}
        >
          {keys.map((key, i) => (
            <div
              key={`${key}-${i}`}
              role={key ? "button" : undefined}
              onClick={() => press(key)}
              style={{
                height: 56,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: key ? "#fff" : "#f5f5f5",
                fontSize: 22,
                cursor: key ? "pointer" : "default",
              }}
            >
              {key === "BACKSPACE" ? <TextDeletionOutline /> : key}
            </div>
          ))}
        </div>
      </div>
    </Popup>
  );
};

const PayPasswordModal = ({
  visible,
  onClose,
  setVisible,
  onSuccess,
  callBack,
}: Props) => {
  const { apiStore, authStore } = useStores();
  const inputRef = useRef<PasscodeInputRef>(null);
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  useEffect(() => {
    if (!visible) {
      setKeyboardVisible(false);
      return undefined;
    }
    setValue("");
    setKeyboardVisible(true);
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [visible]);

  const close = () => {
    setValue("");
    setKeyboardVisible(false);
    setVisible?.(false);
    onClose?.();
  };

  const reopen = () => {
    setValue("");
    setVisible?.(true);
  };

  const onChange = (v: string) => {
    setValue(v.replace(/\D/g, "").slice(0, 6));
  };

  const onVirtualInput = (digit: string) => {
    setValue((prev) => (prev + digit).replace(/\D/g, "").slice(0, 6));
  };

  const onVirtualDelete = () => {
    setValue((prev) => prev.slice(0, -1));
  };

  const submit = async () => {
    if (loading) return;
    if (value.length !== 6) {
      ToastError("请输入6位支付密码");
      return;
    }
    try {
      setLoading(true);
      const res = await apiStore.payCheck({
        token: authStore.getToken,
        pay_pwd: value,
      });
      if (res?.code === 200 || res?.code === 0) {
        ToastSuccess("验证成功");
        onSuccess?.();
        callBack?.(true);
        close();
      } else {
        setValue("");
        close();
        Dialog.confirm({
          content: "密码错误，请重新输入！",
          confirmText: "重新输入",
          cancelText: "取消",
          onConfirm: reopen,
        });
      }
    } catch (e: any) {
      setValue("");
      close();
      Dialog.confirm({
        content: e?.message || "密码错误，请重新输入！",
        confirmText: "重新输入",
        cancelText: "取消",
        onConfirm: reopen,
      });
      callBack?.(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        visible={visible}
        content={
          <div style={{ padding: 12 }}>
            <div style={{ textAlign: "center", marginBottom: 12, fontWeight: 700 }}>
              请输入支付密码
            </div>
            <PasscodeInput
              ref={inputRef}
              value={value}
              onChange={onChange}
              length={6}
              seperated
              caret
              onFocus={() => setKeyboardVisible(true)}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 16,
              }}
            >
              <Button onClick={close} disabled={loading}>
                关闭
              </Button>
              <Button
                color="primary"
                loading={loading}
                disabled={value.length !== 6 || loading}
                onClick={submit}
              >
                确认
              </Button>
            </div>
          </div>
        }
        onClose={close}
        actions={[]}
      />
      <ClickNumberKeyboard
        visible={visible && keyboardVisible}
        onInput={onVirtualInput}
        onDelete={onVirtualDelete}
        onClose={() => setKeyboardVisible(false)}
      />
    </>
  );
};

export default PayPasswordModal;
